import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, GraduationCap, FileText, Bot, BookOpen, ListChecks, MessagesSquare } from "lucide-react";
import { Logo } from "@/components/landing/Logo";
import { NAV_LINKS } from "@/data/content";

const STEPS = [
  "Open your email. Your toolkit link is waiting for you.",
  "Take the 5-minute readiness diagnostic to receive your personalised AI Survey PDF and suggested course.",
  "Start with one small project this week.",
  "Come back to your toolkit page anytime; it always shows the latest resources.",
];

const Art = () => (
  <div className="relative mx-auto aspect-square w-full max-w-[380px]">
    <div className="absolute inset-6 rounded-full bg-gradient-to-br from-teal-100 via-sky-100 to-blue-100 blur-xl" />
    <motion.div initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160, damping: 12, delay: 0.2 }}
      className="absolute left-1/2 top-[10%] grid h-24 w-24 -translate-x-1/2 place-items-center rounded-full bg-[#0E9384] text-white shadow-xl">
      <CheckCircle2 className="h-14 w-14" />
    </motion.div>
    {[[FileText, "left-[8%] top-[42%] bg-blue-50 text-blue-700", 0.4], [Bot, "right-[8%] top-[40%] bg-sky-50 text-sky-700", 0.5], [BookOpen, "left-[22%] bottom-[12%] bg-slate-100 text-[#0B1B33]", 0.6], [ListChecks, "right-[22%] bottom-[14%] bg-teal-50 text-teal-700", 0.7], [MessagesSquare, "left-1/2 -translate-x-1/2 bottom-[30%] bg-blue-50 text-blue-700", 0.8]].map(([I, cls, d], i) => (
      <motion.span key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: d }}
        className={`floaty absolute grid h-16 w-16 place-items-center rounded-2xl shadow-lg ${cls}`} style={{ animationDelay: `${i * 0.6}s` }}>
        <I className="h-8 w-8" />
      </motion.span>
    ))}
  </div>
);

export default function ThankYou() {
  const nav = useNavigate();
  return (
    <div data-testid="thank-you-page" className="min-h-screen bg-[#F7FAFC]">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-[1200px] items-center justify-between px-5 lg:px-10">
          <Logo />
          <button data-testid="thankyou-nav-cta-btn" onClick={() => nav("/go#projects")} className="btn-teal hidden rounded-lg px-5 py-2.5 text-[14px] font-semibold sm:block">Explore Project Examples</button>
        </div>
      </header>
      <main className="mx-auto max-w-[1200px] px-5 py-12 lg:px-10">
        <div className="grid items-center gap-10 md:grid-cols-[0.9fr_1.1fr]">
          <Art />
          <div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="font-display text-5xl font-extrabold tracking-tight text-[#0B1B33] lg:text-6xl">Thank You!</motion.h1>
            <p className="font-display mt-2 text-[24px] font-bold text-[#0E9384] md:text-[30px]">Your Free AI Project Toolkit is on the way.</p>
            <p className="mt-3 text-base text-slate-700 md:text-lg">We've emailed you a link to your toolkit. Please check your inbox (and spam folder). Meanwhile, here are a few next steps:</p>
            <ol className="mt-6 space-y-3">
              {STEPS.map((s, i) => (
                <motion.li key={s} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }} className="flex items-start gap-3 text-[16px] text-slate-800">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#0B1B33] text-[13px] font-bold text-white">{i + 1}</span>{s}
                </motion.li>
              ))}
            </ol>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <button data-testid="thankyou-diagnostic-btn" onClick={() => nav("/go?diagnostic=1")} className="btn-teal flex items-center justify-center gap-2 rounded-xl px-7 py-4 text-[16px] font-semibold">
              Take the 5-Minute Readiness Diagnostic <ArrowRight className="arrow h-5 w-5" />
            </button>
            {localStorage.getItem("aor_toolkit_path") && (
              <button data-testid="thankyou-open-toolkit-btn" onClick={() => nav(localStorage.getItem("aor_toolkit_path"))} className="rounded-xl border border-slate-300 bg-white px-7 py-4 text-[16px] font-semibold text-[#0B1B33] transition-colors hover:bg-slate-50">
                Open My Toolkit
              </button>
            )}
            </div>
          </div>
        </div>
        <div className="mt-12 flex flex-col gap-5 rounded-2xl border border-slate-200 bg-white p-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <GraduationCap className="h-12 w-12 shrink-0 text-blue-600" strokeWidth={1.5} />
            <div><h2 className="font-display text-[22px] font-bold text-[#0B1B33]">Want to go faster?</h2><p className="text-[15px] text-slate-600">Explore the 4-step learning pathway and project examples.</p></div>
          </div>
          <button data-testid="thankyou-explore-btn" onClick={() => nav("/go#pathway")} className="flex items-center justify-center gap-2 rounded-xl bg-[#0B1B33] px-7 py-3.5 font-semibold text-white transition-colors hover:bg-[#12284A]">Explore Now <ArrowRight className="h-4 w-4" /></button>
        </div>
      </main>
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-4 px-5 py-6 md:flex-row md:items-center md:justify-between lg:px-10">
          <Logo sub="Turn Experience into Opportunities" testId="thankyou-footer-logo" />
          <nav className="flex flex-wrap gap-4 text-[14px] text-slate-700">{NAV_LINKS.map((l) => <a key={l.href} href={`/${l.href}`} className="hover:text-[#0E9384]">{l.label}</a>)}</nav>
        </div>
      </footer>
    </div>
  );
}
